/**
 * A configurator-supplied colour, or nothing — SIMS-8531 review.
 *
 * 🔴 `EventResult.displayColor` is authored on the Events screen and persisted,
 * and the only thing constraining its shape is a client-side hex check on that
 * form. The server's DTO does not validate the field, so a value written by any
 * other path — an import, a direct API call, a future generator — reaches the
 * browser unchecked and is handed straight to a `style` attribute.
 *
 * The realistic worst case is modest: a `style` attribute cannot execute script
 * in any browser this app supports, so the exposure is an outbound request via
 * `url(...)` rather than XSS. But this is a HIPAA/HITRUST environment where an
 * unexpected outbound request from an authenticated staff session is its own
 * problem, and the check costs one regex.
 *
 * Returns `undefined` rather than a default colour on a non-match, so a caller
 * falls back to its own theme value and a malformed colour renders as "no
 * colour set" rather than as some other result's colour.
 */
const HEX =
	/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** MUI theme tokens (`text.primary`, `warning.dark`) are ours, not user input. */
const THEME_TOKEN = /^[a-z]+(?:\.[a-zA-Z]+)?$/;

export const safeColor = (value?: string | null): string | undefined => {
	if (!value) return undefined;

	const trimmed = value.trim();
	if (HEX.test(trimmed)) return trimmed;
	// A theme token is a literal this codebase wrote, never a stored value —
	// `ActivityResultCell` passes both kinds through the same prop.
	if (THEME_TOKEN.test(trimmed)) return trimmed;

	return undefined;
};

export default safeColor;
